import React, { useState, useEffect } from 'react';
import { User } from 'lucide-react';
import { getProxiedImageUrl } from '../../utils/imageUtils';

interface AvatarProps {
  url: string | null;
  name?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const Avatar: React.FC<AvatarProps> = ({
  url,
  name = 'User',
  size = 'md',
  className = '',
}) => {
  const [imageError, setImageError] = useState(false);
  
  // Handle image load error
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    console.error('❌ Erro ao carregar avatar:', {
      originalSrc: target.src,
      url: url,
      name: name
    });
    
    setImageError(true);
    target.onerror = null; // Prevent infinite loop
  };
  
  // Reset error state when URL changes
  useEffect(() => {
    setImageError(false);
  }, [url]);
  
  // Generate a consistent color based on the name
  const getColorFromName = (name: string): string => {
    const colors = [
      'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500',
      'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'
    ];
    
    // Simple hash function to get a consistent index
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Get a positive index within the colors array range
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-24 h-24',
  };

  const colorClass = getColorFromName(name);

  // Se há URL mas houve erro, mostrar fallback
  const shouldShowFallback = !url || imageError;

  return (
    <div className={`relative ${className}`}>
      {!shouldShowFallback ? (
        <img
          src={getProxiedImageUrl(url)}
          alt={name || 'User avatar'}
          className={`${sizeClasses[size]} rounded-full object-cover`}
          onError={handleImageError}
          crossOrigin="anonymous"
          key={url} // Force re-render when URL changes
        />
      ) : (
        <div
          className={`${sizeClasses[size]} rounded-full ${colorClass} flex items-center justify-center text-white font-medium`}
        >
          {name ? name.charAt(0).toUpperCase() : (
            <User
              className={`${
                size === 'sm' ? 'w-4 h-4' :
                size === 'md' ? 'w-6 h-6' :
                size === 'lg' ? 'w-8 h-8' :
                'w-12 h-12'
              } text-white`}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Avatar;
